const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

let razorpayScriptPromise = null;

const closePaymentWindow = (paymentWindow) => {
  if (paymentWindow && !paymentWindow.closed) {
    paymentWindow.close();
  }
};

export const loadRazorpayScript = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Razorpay is unavailable on the server."));
  }

  if (window.Razorpay) {
    return Promise.resolve(window.Razorpay);
  }

  if (razorpayScriptPromise) {
    return razorpayScriptPromise;
  }

  razorpayScriptPromise = new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId = null;

    const finalize = (callback) => {
      if (settled) {
        return;
      }

      settled = true;

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      callback();
    };

    const resolveScript = () => {
      finalize(() => {
        if (window.Razorpay) {
          resolve(window.Razorpay);
          return;
        }

        razorpayScriptPromise = null;
        reject(new Error("Razorpay failed to initialize."));
      });
    };

    const rejectScript = () => {
      finalize(() => {
        razorpayScriptPromise = null;
        reject(new Error("Failed to load Razorpay checkout."));
      });
    };

    const existingScript = document.querySelector(
      `script[src="${RAZORPAY_SCRIPT_SRC}"]`,
    );

    timeoutId = window.setTimeout(() => {
      rejectScript();
    }, 8000);

    if (existingScript) {
      existingScript.addEventListener("load", resolveScript, { once: true });
      existingScript.addEventListener("error", rejectScript, { once: true });

      if (window.Razorpay) {
        resolveScript();
      }

      return;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT_SRC;
    script.async = true;
    script.onload = resolveScript;
    script.onerror = rejectScript;
    document.body.appendChild(script);
  });

  return razorpayScriptPromise;
};

export const payWithRazorpay = async (data, options = {}) => {
  const { paymentWindow = null } = options;

  if (!data) {
    throw new Error("Payment data is missing.");
  }

  if (data?.payment_type === "single_payment") {
    const paymentUrl = data?.payment_link?.url;

    if (!paymentUrl) {
      throw new Error("No Razorpay payment link found.");
    }

    if (paymentWindow && !paymentWindow.closed) {
      paymentWindow.location.replace(paymentUrl);
    } else {
      window.location.assign(paymentUrl);
    }

    return {
      msg: "Redirecting to payment link...",
      success: true,
    };
  }

  const Razorpay = await loadRazorpayScript();

  if (!data?.payment_link) {
    closePaymentWindow(paymentWindow);
    throw new Error("No Razorpay checkout payload found.");
  }

  closePaymentWindow(paymentWindow);

  return new Promise((resolve) => {
    const checkout = new Razorpay({
      ...data.payment_link,
      theme: { color: "#00B3B0" },
      handler: (response) => {
        resolve({
          msg: "Payment successful!",
          success: true,
          response,
        });
      },
      modal: {
        ondismiss: () => {
          resolve({ msg: "Payment modal closed", success: false });
        },
      },
    });

    checkout.open();
  });
};
